import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useOrganisation, useCreateOrganisation, useUpdateOrganisation } from "@/hooks/use-admin-organisations"
import { CreateOrganisationData, UpdateOrganisationData } from "@/hooks/use-admin-organisations"

interface OrganisationFormProps {
  organisationId?: string
  onSuccess: () => void
  onCancel: () => void
}

export function OrganisationForm({ organisationId, onSuccess, onCancel }: OrganisationFormProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [website, setWebsite] = useState("")
  const [industry, setIndustry] = useState("")

  const { data: organisation } = useOrganisation(organisationId || "")
  const createOrganisation = useCreateOrganisation()
  const updateOrganisation = useUpdateOrganisation()

  useEffect(() => {
    if (organisation) {
      setName(organisation.name || "")
      setDescription(organisation.description || "")
      setWebsite(organisation.website || "")
      setIndustry(organisation.industry || "")
    }
  }, [organisation])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (organisationId) {
      const updateData: UpdateOrganisationData = {
        name: name || undefined,
        description: description || undefined,
        website: website || undefined,
        industry: industry || undefined,
      }
      await updateOrganisation.mutateAsync({ organisationId, data: updateData })
    } else {
      if (!name) {
        return
      }
      const createData: CreateOrganisationData = {
        name,
        description: description || undefined,
        website: website || undefined,
        industry: industry || undefined,
      }
      await createOrganisation.mutateAsync(createData)
    }

    onSuccess()
  }

  const isPending = createOrganisation.isPending || updateOrganisation.isPending

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="name">Name *</Label>
        <Input
          id="name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Organisation name"
          required={!organisationId}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Organisation description"
          rows={4}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="website">Website</Label>
        <Input
          id="website"
          type="url"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="https://example.com"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="industry">Industry</Label>
        <Input
          id="industry"
          value={industry}
          onChange={(e) => setIndustry(e.target.value)}
          placeholder="e.g., Technology, Healthcare, Finance"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending || (!organisationId && !name)}>
          {isPending ? "Saving..." : organisationId ? "Update organisation" : "Create organisation"}
        </Button>
      </div>
    </form>
  )
}

